import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminReferralSalesClient, salesAgentClient, publicReferralClient } from '@/api/grpc/services';

/**
 * Hook for Referral Sales Management
 * 
 * Provides unified access to all Sales Referral gRPC operations.
 * Uses React Query for state management, caching, and optimistic updates.
 */

// ---- Admin Hooks ----

export function useAdminSalesProducts(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['admin', 'sales-products', page, limit],
    queryFn: () => adminReferralSalesClient.listProducts({ page, limit }),
  });
}

export function useAdminProductCatalog(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['admin', 'product-catalog', page, limit],
    queryFn: () => adminReferralSalesClient.listProductCatalog({ page, limit }),
  });
}

export function useAdminSalesAgents(status = '', page = 1, limit = 20) {
  return useQuery({
    queryKey: ['admin', 'sales-agents', status, page, limit],
    queryFn: () => adminReferralSalesClient.listSalesAgents({ status: status || undefined, page, limit }),
  });
}

export function useAdminAttributions(page = 1, limit = 20, status?: string) {
  return useQuery({
    queryKey: ['admin', 'attributions', page, limit, status],
    queryFn: () => adminReferralSalesClient.listAttributions({ page, limit, status }),
  });
}

export function useAdminDashboardStats() {
  return useQuery({
    queryKey: ['admin', 'sales-dashboard-stats'],
    queryFn: () => adminReferralSalesClient.getReferralDashboardStats({}),
  });
}

export function useVerifyAttribution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; revenueCents: number }) => 
      adminReferralSalesClient.verifyAttribution({ id: args.id, revenueCents: BigInt(args.revenueCents) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'attributions'] });
    },
  });
}

export function useApproveSalesAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => 
      adminReferralSalesClient.approveSalesAgent({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sales-agents'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'sales-dashboard-stats'] });
    },
  });
}

export function useAdminSalesOnboardingConfig() {
  return useQuery({
    queryKey: ['admin', 'sales-onboarding-config'],
    queryFn: () => adminReferralSalesClient.getSalesOnboardingConfig({}),
  });
}

export function useUpdateSalesOnboardingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (youtubeVideoUrl: string) => 
      adminReferralSalesClient.updateSalesOnboardingConfig({ youtubeVideoUrl }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sales-onboarding-config'] });
      queryClient.invalidateQueries({ queryKey: ['agent', 'sales-onboarding-config'] });
    },
  });
}

export function useCreateSalesProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (product: any) => adminReferralSalesClient.createProduct({ product }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sales-products'] });
    },
  });
}

export function useUpdateSalesProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (product: any) => adminReferralSalesClient.updateProduct({ product }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'sales-products'] });
    },
  });
}

export function useCreateProductCatalogEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (product: any) => adminReferralSalesClient.createProductCatalogEntry({ product }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'product-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['public', 'product-catalog'] });
    },
  });
}

export function useUpdateProductCatalogEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (product: any) => adminReferralSalesClient.updateProductCatalogEntry({ product }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'product-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['public', 'product-catalog'] });
    },
  });
}

export function useDeleteProductCatalogEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminReferralSalesClient.deleteProductCatalogEntry({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'product-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['public', 'product-catalog'] });
    },
  });
}

export function usePublicProductCatalog(page = 1, limit = 50) {
  return useQuery({
    queryKey: ['public', 'product-catalog', page, limit],
    queryFn: () => publicReferralClient.listProductCatalog({ page, limit }),
  });
}

// ---- Agent Hooks ----

export function useAgentDashboard() {
  return useQuery({
    queryKey: ['agent', 'dashboard'],
    queryFn: () => salesAgentClient.getAgentDashboard({}),
  });
}

export function useOnboardingProfile() {
  return useQuery({
    queryKey: ['agent', 'onboarding-profile'],
    queryFn: () => salesAgentClient.getOnboardingProfile({}),
  });
}

export function useLeads(productId?: string, status?: string) {
  return useQuery({
    queryKey: ['agent', 'leads', productId, status],
    queryFn: () => salesAgentClient.listLeads({ productId, status, limit: 100 }),
  });
}

export function useSearchStrategies(productId?: string) {
  return useQuery({
    queryKey: ['agent', 'search-strategies', productId],
    queryFn: () => salesAgentClient.getSearchStrategies({ productId }),
  });
}

export function useLeadTableTasks() {
  return useQuery({
    queryKey: ['agent', 'lead-table-tasks'],
    queryFn: () => salesAgentClient.getLeadTableTasks({}),
  });
}

export function useMessageStrategy(taskId: string) {
  return useQuery({
    queryKey: ['agent', 'message-strategy', taskId],
    queryFn: () => salesAgentClient.getMessageStrategy({ taskId }),
    enabled: !!taskId,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lead: any) => salesAgentClient.createLead({ lead }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['agent', 'lead-table-tasks'] });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (lead: any) => salesAgentClient.updateLead({ lead }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['agent', 'dashboard'] });
    },
  });
}

export function useUpdateLeadStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; newStatus: string; responseType?: string }) => 
      salesAgentClient.updateLeadStatus({ id: args.id, newStatus: args.newStatus, responseType: args.responseType }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['agent', 'dashboard'] });
    },
  });
}

export function useSubmitForStrategy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => salesAgentClient.submitForStrategy({ taskId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', 'lead-table-tasks'] });
    },
  });
}

export function useRegenerateStrategies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId?: string) => salesAgentClient.regenerateSearchStrategies({ productId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent', 'search-strategies'] });
      queryClient.invalidateQueries({ queryKey: ['agent', 'dashboard'] });
    },
  });
}

export function useSalesOnboardingConfig() {
  return useQuery({
    queryKey: ['agent', 'sales-onboarding-config'],
    queryFn: () => salesAgentClient.getOnboardingConfig({}),
  });
}

export function useMyReferralLinks() {
  return useQuery({
    queryKey: ['agent', 'referral-links'],
    queryFn: () => salesAgentClient.listMyReferralLinks({}),
  });
}

export function useReferralLinkStats(productId: string) {
  return useQuery({
    queryKey: ['agent', 'referral-link-stats', productId],
    queryFn: () => salesAgentClient.getReferralLinkStats({ productId }),
    enabled: !!productId,
  });
}

export function useMyAttributions(page = 1, limit = 100) {
  return useQuery({
    queryKey: ['agent', 'my-attributions', page, limit],
    queryFn: () => salesAgentClient.listMyAttributions({ page, limit }),
  });
}


